/**
 * Direct Messaging Service
 * Frontend service for sending and managing Direct protocol messages via HISP
 */

import { getAuthToken } from '../utils/authUtils';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Content types for Direct messages
 */
export const DirectMessageContentType = {
  CCDA: 'application/xml',
  PDF: 'application/pdf',
  TEXT: 'text/plain',
  HL7: 'application/hl7-v2'
};

/**
 * Document types for clinical documents
 */
export const DocumentType = {
  CCD: 'Continuity of Care Document',
  REFERRAL: 'Referral',
  DISCHARGE_SUMMARY: 'Discharge Summary',
  LAB_RESULTS: 'Lab Results',
  IMAGING_RESULTS: 'Imaging Results',
  PROGRESS_NOTE: 'Progress Note',
  CONSULTATION: 'Consultation',
  CARE_PLAN: 'Care Plan',
  GENERAL: 'General'
};

/**
 * Send a Direct message
 * @param {Object} params - Message parameters
 * @param {string} params.to - Recipient Direct address
 * @param {string} params.subject - Message subject
 * @param {string} params.body - Message body
 * @param {string} [params.contentType] - Content type (default: text/plain)
 * @param {string} [params.hispConnectionId] - HISP connection ID (auto-detected if not provided)
 * @param {string} [params.facilityId] - Facility ID for sender context
 * @param {string} [params.patientId] - Associated patient ID
 * @param {string} [params.encounterId] - Associated encounter ID
 * @param {string} [params.documentType] - Type of document
 * @param {Array} [params.attachments] - File attachments
 * @returns {Promise<Object>} Send result
 */
export async function sendDirectMessage(params) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/directMessaging/direct/send`,
      {
        to: params.to,
        subject: params.subject,
        body: params.body,
        contentType: params.contentType || DirectMessageContentType.TEXT,
        hispConnectionId: params.hispConnectionId,
        facilityId: params.facilityId,
        patientId: params.patientId,
        encounterId: params.encounterId,
        documentType: params.documentType,
        attachments: params.attachments,
        metadata: params.metadata
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('[DirectMessaging] Error sending message:', error);
    throw error.response?.data || error;
  }
}

/**
 * Send a clinical document via Direct
 * @param {Object} params - Document parameters
 * @param {string} params.to - Recipient Direct address
 * @param {string} params.documentType - Type of document (e.g., 'CCD', 'Referral')
 * @param {string} params.documentContent - Base64 encoded document content
 * @param {string} [params.documentFormat] - Document format ('CCDA', 'PDF', 'HL7')
 * @param {string} [params.hispConnectionId] - HISP connection ID
 * @param {string} [params.facilityId] - Facility ID
 * @param {string} [params.patientId] - Patient ID
 * @param {string} [params.patientName] - Patient name for subject line
 * @param {string} [params.encounterId] - Encounter ID
 * @returns {Promise<Object>} Send result
 */
export async function sendClinicalDocument(params) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/directMessaging/direct/send-document`,
      {
        to: params.to,
        documentType: params.documentType,
        documentContent: params.documentContent,
        documentFormat: params.documentFormat || 'CCDA',
        hispConnectionId: params.hispConnectionId,
        facilityId: params.facilityId,
        patientId: params.patientId,
        patientName: params.patientName,
        encounterId: params.encounterId,
        metadata: params.metadata
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('[DirectMessaging] Error sending document:', error);
    throw error.response?.data || error;
  }
}

/**
 * Get Direct message logs
 * @param {Object} params - Query parameters
 * @param {string} params.facilityId - Facility ID
 * @param {string} [params.direction] - 'outbound' or 'inbound'
 * @param {string} [params.status] - Message status
 * @param {Date} [params.startDate] - Start date filter
 * @param {Date} [params.endDate] - End date filter
 * @param {number} [params.limit] - Max results
 * @returns {Promise<Object>} Message logs
 */
export async function getDirectMessageLogs(params) {
  try {
    const token = await getAuthToken();
    
    const queryParams = new URLSearchParams();
    if (params.facilityId) queryParams.append('facilityId', params.facilityId);
    if (params.direction) queryParams.append('direction', params.direction);
    if (params.status) queryParams.append('status', params.status);
    if (params.startDate) queryParams.append('startDate', params.startDate.toISOString());
    if (params.endDate) queryParams.append('endDate', params.endDate.toISOString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/directMessaging/direct/logs?${queryParams.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('[DirectMessaging] Error getting logs:', error);
    throw error.response?.data || error;
  }
}

/**
 * Get a specific message log
 * @param {string} logId - Message log ID
 * @returns {Promise<Object>} Message log details
 */
export async function getDirectMessageLog(logId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/directMessaging/direct/logs/${logId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('[DirectMessaging] Error getting log:', error);
    throw error.response?.data || error;
  }
}

/**
 * Get available HISP connections for the current user
 * @param {string} [facilityId] - Optional facility ID filter
 * @returns {Promise<Object>} Available HISP connections
 */
export async function getAvailableHispConnections(facilityId) {
  try {
    const token = await getAuthToken();
    
    const queryParams = new URLSearchParams();
    if (facilityId) queryParams.append('facilityId', facilityId);
    
    const url = `${API_BASE}/directMessaging/direct/connections${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    const response = await axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data.connections || [];
  } catch (error) {
    console.error('[DirectMessaging] Error getting connections:', error);
    throw error.response?.data || error;
  }
}

/**
 * Utility: Convert file to base64
 * @param {File} file - File object
 * @returns {Promise<string>} Base64 encoded content
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Utility: Create attachment from file
 * @param {File} file - File object
 * @returns {Promise<Object>} Attachment object
 */
export async function createAttachmentFromFile(file) {
  const content = await fileToBase64(file);
  return {
    filename: file.name,
    content,
    contentType: file.type || 'application/octet-stream',
    encoding: 'base64'
  };
}

